/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Search,
  Filter,
  ChevronDown,
  XCircle,
  MoreHorizontal,
} from "lucide-react";
import Pagination from "../../../components/Pagination";
import { AdminContext } from "../../../context/AdminContext";
import { rawStatus, statusMap } from "../../../utils/constants";

const BookPurchases = () => {
  const { bookPurchases, fetchAllBookPurchases, updateBookPurchaseStatus } =
    useContext(AdminContext);
  const [normalizedOrders, setNormalizedOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState(""); // Separate input state
  const [statusFilter, setStatusFilter] = useState("All Statuses");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const statusDropdownRef = useRef(null);

  useEffect(() => {
    fetchAllBookPurchases();
  }, []);

  useEffect(() => {
    setNormalizedOrders(
      bookPurchases.map((o) => ({
        ...o,
        id: o.id || "unknown",
        customer: {
          name: o.user?.name || o.name || "Unknown User",
          email: o.user?.phone || o.user?.email || o.phone || o.email || "N/A",
          avatar:
            o.user?.image ||
            `https://ui-avatars.com/api/?name=${encodeURIComponent(
              o.user?.name || o.name || "Unknown",
            )}&background=random`,
        },
        bookTitle: o.bookTitle || o.book?.title || "N/A",
        quantity: o.quantity || 1,
        totalAmount: o.amount || o.totalAmount || 0,
        orderDate: o.createdAt
          ? new Date(o.createdAt).toLocaleDateString()
          : "N/A",
        status: (o.status || "pending").toLowerCase(),
        deliveryAddress: o.deliveryAddress || "N/A",
      })),
    );
  }, [bookPurchases]);

  const filteredOrders = normalizedOrders.filter((o) => {
    const matchesSearch =
      (o.customer.name || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (o.customer.email || "").includes(searchTerm) ||
      (o.id || "").toLowerCase().includes(searchTerm.toLowerCase());

    // Handle status filter
    let matchesStatus = true;
    if (statusFilter === "Pending") {
      matchesStatus = o.status === "pending";
    } else if (statusFilter === "Delivered") {
      matchesStatus = o.status === "delivered";
    } else if (statusFilter === "Cancelled") {
      matchesStatus = o.status === "cancelled";
    }
    // "All Statuses" includes everything

    return matchesSearch && matchesStatus;
  });

  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, itemsPerPage]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleStatusUpdate = (newDisplayStatus) => {
    if (selectedOrder) {
      const newRawStatus = rawStatus[newDisplayStatus];
      updateBookPurchaseStatus(selectedOrder.id, newRawStatus);
      setShowUpdateModal(false);
      setSelectedOrder(null);
    }
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setShowStatusDropdown(false);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setSearchInput("");
    setStatusFilter("All Statuses");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "delivered":
        return "bg-emerald-600 text-white dark:bg-emerald-700 dark:text-white border-emerald-700 dark:border-emerald-800";
      case "cancelled":
        return "bg-red-600 text-white dark:bg-red-700 dark:text-white border-red-700 dark:border-red-800";
      default:
        return "bg-yellow-600 text-white dark:bg-yellow-700 dark:text-white border-yellow-700 dark:border-yellow-800";
    }
  };

  const handleCloseModal = (e) => {
    if (e.target === e.currentTarget) {
      setShowUpdateModal(false);
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text dark:text-dark-text">
            Book Purchases
          </h1>
          <p className="text-gray-500 dark:text-dark-muted mt-1">
            Manage book orders and delivery status.
          </p>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border hover:bg-accent dark:hover:bg-gray-700 transition-colors w-full sm:w-auto"
        >
          <Filter size={20} className="text-gray-400" />
          {showFilters ? "Hide Filters" : "Show Filters"}
        </button>
      </div>

      {/* Filter Bar */}
      {showFilters && (
        <div className="bg-card dark:bg-dark-surface p-1.5 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-border mb-6 flex gap-2">
          {/* Search Input */}
          <div className="relative flex-1">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Search by customer or Order ID..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              className="w-full pl-12 pr-4 py-3 rounded-xl bg-accent dark:bg-gray-900 border border-transparent focus:bg-card dark:focus:bg-dark-surface focus:border-primary outline-none text-sm transition-all text-text dark:text-dark-text placeholder-gray-400"
            />
          </div>

          {/* Search Button */}
          <button
            onClick={handleSearch}
            className="flex items-center justify-center px-4 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl text-sm font-medium transition-colors"
          >
            <Search size={16} />
          </button>

          {/* Status Filter Dropdown */}
          <div className="relative" ref={statusDropdownRef}>
            <button
              onClick={() => setShowStatusDropdown(!showStatusDropdown)}
              className="flex items-center gap-2 px-4 py-3 bg-accent dark:bg-gray-900 text-gray-600 dark:text-dark-muted rounded-xl text-sm font-medium border border-transparent hover:border-gray-200 dark:hover:border-dark-border transition-colors whitespace-nowrap"
            >
              <Filter size={16} />
              {statusFilter}
              <ChevronDown
                size={14}
                className={`transition-transform ${
                  showStatusDropdown ? "rotate-180" : ""
                }`}
              />
            </button>

            {showStatusDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 animate-in fade-in zoom-in-95 duration-200">
                {["All Statuses", "Pending", "Delivered", "Cancelled"].map(
                  (status) => (
                    <button
                      key={status}
                      onClick={() => handleStatusFilter(status)}
                      className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        statusFilter === status
                          ? "bg-primary text-white dark:bg-primary"
                          : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                      }`}
                    >
                      {status}
                    </button>
                  ),
                )}
              </div>
            )}
          </div>

          {/* Reset Button */}
          <button
            onClick={resetFilters}
            className="px-4 py-3 rounded-xl bg-accent dark:bg-gray-900 text-text dark:text-dark-text font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            Reset
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border overflow-hidden">
        <div className="overflow-x-auto min-h-[400px]">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-500 uppercase bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-4 sm:px-6 py-4 font-bold tracking-wider">
                  Order ID
                </th>
                <th className="px-4 sm:px-6 py-4 font-bold tracking-wider">
                  Customer
                </th>
                <th className="px-4 sm:px-6 py-4 font-bold tracking-wider">
                  Quantity
                </th>
                <th className="px-4 sm:px-6 py-4 font-bold tracking-wider">
                  Date
                </th>
                <th className="px-4 sm:px-6 py-4 font-bold tracking-wider">
                  Amount
                </th>
                <th className="px-4 sm:px-6 py-4 font-bold tracking-wider">
                  Status
                </th>
                <th className="px-4 sm:px-6 py-4 font-bold tracking-wider text-center">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {paginatedOrders.map((order) => (
                <tr
                  key={order.id || Math.random()}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                >
                  <td className="px-4 sm:px-6 py-4 font-mono text-gray-500 text-xs sm:text-sm">
                    <span className="hidden sm:inline">
                      {order.id || "N/A"}
                    </span>
                    <span className="sm:hidden">
                      {order.id ? order.id.substring(0, 8) + "..." : "N/A"}
                    </span>
                  </td>
                  <td className="px-4 sm:px-6 py-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={
                          order.customer?.avatar ||
                          `https://ui-avatars.com/api/?name=Unknown&background=random`
                        }
                        className="hidden md:block w-8 h-8 rounded-full object-cover"
                        alt={order.customer?.name || "Unknown"}
                      />
                      <div>
                        <p className="font-bold text-text dark:text-dark-text">
                          {order.customer?.name || "Unknown"}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-4">
                    <p className="font-medium text-gray-800 dark:text-dark-text">
                      {order.quantity || 1}
                    </p>
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-gray-600 dark:text-dark-muted">
                    {order.orderDate || "N/A"}
                  </td>
                  <td className="px-4 sm:px-6 py-4 font-bold text-primary">
                    ETB {order.totalAmount || 0}
                  </td>
                  <td className="px-4 sm:px-6 py-4">
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-bold border ${getStatusColor(
                        order.status,
                      )}`}
                    >
                      {statusMap[order.status] || order.status || "Unknown"}
                    </span>
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-center">
                    <button
                      onClick={() => {
                        setSelectedOrder(order);
                        setShowUpdateModal(true);
                      }}
                      className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <MoreHorizontal size={18} />
                    </button>
                  </td>
                </tr>
              ))}
              {paginatedOrders.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    No orders found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="border-t border-gray-100 dark:border-dark-border bg-gray-50/50 dark:bg-dark-surface p-2">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </div>
      </div>

      {/* Update Modal */}
      {showUpdateModal && selectedOrder && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={handleCloseModal}
        >
          <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200 p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-text dark:text-dark-text">
                Update Order {selectedOrder.id}
              </h3>
              <button onClick={() => setShowUpdateModal(false)}>
                <XCircle className="text-gray-400 hover:text-gray-600" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl space-y-2">
                <p className="text-sm flex justify-between">
                  <span className="text-gray-500 dark:text-dark-muted">
                    Customer:
                  </span>{" "}
                  <span className="font-medium text-text dark:text-dark-text">
                    {selectedOrder.customer.name}
                  </span>
                </p>
                <p className="text-sm flex justify-between">
                  <span className="text-gray-500 dark:text-dark-muted">
                    Quantity:
                  </span>{" "}
                  <span className="font-medium text-text dark:text-dark-text">
                    {selectedOrder.quantity}
                  </span>
                </p>
                <p className="text-sm flex justify-between">
                  <span className="text-gray-500 dark:text-dark-muted">
                    Amount:
                  </span>{" "}
                  <span className="font-bold text-primary">
                    ETB {selectedOrder.totalAmount}
                  </span>
                </p>
                <p className="text-sm flex justify-between">
                  <span className="text-gray-500 dark:text-dark-muted">
                    Current Status:
                  </span>{" "}
                  <span className="font-medium text-text dark:text-dark-text">
                    {statusMap[selectedOrder.status] || selectedOrder.status}
                  </span>
                </p>
              </div>
            </div>

            {(statusMap[selectedOrder.status] || selectedOrder.status) ===
            "Pending" ? (
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleStatusUpdate("Cancelled")}
                  className="py-2.5 bg-red-50 text-red-600 border border-red-200 rounded-xl font-bold hover:bg-red-100 transition-colors"
                >
                  Cancel Order
                </button>
                <button
                  onClick={() => handleStatusUpdate("Delivered")}
                  className="py-2.5 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-200 dark:shadow-none"
                >
                  Mark Delivered
                </button>
              </div>
            ) : (
              <p className="text-center text-gray-500 dark:text-dark-muted text-sm">
                No further actions available for{" "}
                {statusMap[selectedOrder.status] || selectedOrder.status}{" "}
                orders.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookPurchases;
